import type { PersonRef } from '@ralph/contracts'
import clsx from 'clsx'
import { ClipboardList, Search, X } from 'lucide-react'
import { type KeyboardEvent, useEffect, useId, useState } from 'react'
import { Button, Spinner, Textarea } from '@/components/ui/primitives.tsx'
import { usePeopleSearch, useResolvePeople } from '@/hooks/use-groups.ts'
import { useI18n } from '@/i18n/index.ts'

/**
 * Finding people by name or email, or by pasting a list of addresses (PRD-26 D-8).
 *
 * Managers and administrators only — the server refuses everyone else, and the surfaces that use
 * this are theirs. Only active people come back, never a service account. Somebody who has never
 * signed in has no account yet (D-5), so a pasted address with no account is listed as such
 * rather than silently dropped: the person holding the list needs to know who to chase.
 *
 * `multiple` collects chips (adding members); without it the first choice is the answer
 * (appointing a manager, choosing an owner).
 */

/** Addresses out of whatever was pasted: commas, semicolons, spaces, new lines, "Name <a@b>". */
export const parseEmails = (text: string): string[] => {
  const matches = text.match(/[^\s,;<>"']+@[^\s,;<>"']+/g) ?? []
  return [...new Set(matches.map((email) => email.trim().toLowerCase()))].slice(0, 200)
}

export const PersonPicker = ({
  chosen,
  onChange,
  multiple = false,
  exclude = [],
  disabled = false,
  autoFocus = false
}: {
  chosen: PersonRef[]
  onChange: (next: PersonRef[]) => void
  multiple?: boolean
  /** People who cannot be picked here (already members, the current owner). */
  exclude?: readonly string[]
  disabled?: boolean
  autoFocus?: boolean
}) => {
  const fmt = useI18n()
  const t = fmt.t.people.picker
  const inputId = useId()
  const listId = useId()
  const pasteId = useId()
  const [mode, setMode] = useState<'search' | 'paste'>('search')
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [active, setActive] = useState(0)
  const [pasted, setPasted] = useState('')
  const [notFound, setNotFound] = useState<string[]>([])
  const resolve = useResolvePeople()

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 200)
    return () => clearTimeout(timer)
  }, [query])

  const search = usePeopleSearch(debounced, mode === 'search' && !disabled)
  const results = (search.data?.items ?? []).filter(
    (person) => !exclude.includes(person.id) && !chosen.some((picked) => picked.id === person.id)
  )
  const open = mode === 'search' && debounced.length >= 2

  const add = (people: PersonRef[]) => {
    const fresh = people.filter((person) => !exclude.includes(person.id) && !chosen.some((p) => p.id === person.id))
    onChange(multiple ? [...chosen, ...fresh] : fresh.slice(0, 1))
    setQuery('')
    setActive(0)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActive((current) => (results.length === 0 ? 0 : (current + step + results.length) % results.length))
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      const person = results[active]
      if (person) add([person])
      return
    }
    if (event.key === 'Escape' && query !== '') {
      event.preventDefault()
      event.stopPropagation()
      setQuery('')
      return
    }
    if (event.key === 'Backspace' && query === '' && multiple && chosen.length > 0) {
      onChange(chosen.slice(0, -1))
    }
  }

  const announce =
    mode !== 'search' || debounced.length < 2
      ? ''
      : search.isError
        ? t.failed
        : search.isLoading
          ? ''
          : results.length === 0
            ? t.noMatch(debounced)
            : t.results(fmt, results.length)

  return (
    <div className="min-w-0">
      {multiple && chosen.length > 0 && (
        <ul aria-label={t.chosen} className="mb-2 flex flex-wrap gap-1.5">
          {chosen.map((person) => (
            <li key={person.id}>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-rule-strong bg-card-solid py-[3px] pl-[3px] pr-2 text-[12.5px] shadow-glass">
                <Initials person={person} size={20} />
                <span className="font-medium tracking-[-0.012em]">{person.displayName}</span>
                <button
                  type="button"
                  aria-label={t.remove(person.displayName)}
                  disabled={disabled}
                  onClick={() => onChange(chosen.filter((p) => p.id !== person.id))}
                  className="-mr-1 ml-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full text-fg-subtle hover:bg-card hover:text-fg"
                >
                  <X size={11} aria-hidden />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {mode === 'search' ? (
        <>
          <div className="relative">
            <label htmlFor={inputId} className="sr-only">
              {t.searchLabel}
            </label>
            <Search size={13} aria-hidden className="pointer-events-none absolute left-3 top-[10px] text-fg-subtle" />
            <input
              id={inputId}
              role="combobox"
              aria-expanded={open}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={open && results[active] ? `${listId}-${active}` : undefined}
              value={query}
              disabled={disabled}
              // biome-ignore lint/a11y/noAutofocus: the dialog that holds this has one job, and this is it.
              autoFocus={autoFocus}
              autoComplete="off"
              placeholder={t.searchPlaceholder}
              onChange={(event) => {
                setQuery(event.target.value)
                setActive(0)
              }}
              onKeyDown={onKeyDown}
              className="h-[34px] w-full rounded-tile border border-rule-strong bg-card-solid pl-8 pr-3 text-[13px] text-fg placeholder:text-fg-subtle focus:border-primary focus:outline-none"
            />
          </div>
          <p aria-live="polite" className="sr-only">
            {announce}
          </p>
          {query.trim().length > 0 && query.trim().length < 2 && (
            <p className="mt-1.5 text-[12px] text-fg-subtle">{t.minChars}</p>
          )}
          {open && (
            <div
              id={listId}
              role="listbox"
              aria-label={t.searchLabel}
              className="mt-1.5 max-h-[15rem] overflow-y-auto rounded-tile border border-rule bg-card-solid p-1 shadow-glass"
            >
              {search.isLoading && (
                <p className="flex items-center gap-2 px-2 py-2 text-[12.5px] text-fg-subtle">
                  <Spinner />
                </p>
              )}
              {search.isError && <p className="px-2 py-2 text-[12.5px] text-alert">{t.failed}</p>}
              {!search.isLoading && !search.isError && results.length === 0 && (
                <p className="px-2 py-2 text-[12.5px] text-fg-muted">{t.noMatch(debounced)}</p>
              )}
              {results.map((person, index) => (
                // A button with `tabIndex={-1}`: focus stays in the combobox, which reaches it with
                // aria-activedescendant — the command palette's pattern.
                <button
                  key={person.id}
                  id={`${listId}-${index}`}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  aria-selected={index === active}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseMove={() => setActive(index)}
                  onClick={() => add([person])}
                  className={clsx(
                    'flex w-full cursor-pointer items-center gap-2.5 rounded-chip px-2 py-1.5 text-left',
                    index === active ? 'bg-card shadow-[inset_0_0_0_1px_var(--rule-strong)]' : 'hover:bg-card'
                  )}
                >
                  <Initials person={person} size={28} />
                  <span className="min-w-0">
                    <span className="block truncate text-[13px] font-semibold tracking-[-0.012em]">
                      {person.displayName}
                    </span>
                    <span className="block truncate text-[12px] text-fg-muted">{person.email}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {multiple && (
            <button
              type="button"
              onClick={() => setMode('paste')}
              className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] text-fg-muted hover:text-fg"
            >
              <ClipboardList size={12} aria-hidden />
              {t.pasteToggle}
            </button>
          )}
        </>
      ) : (
        <div>
          <label htmlFor={pasteId} className="mb-1 block text-[12.5px] text-fg-muted">
            {t.pasteLabel}
          </label>
          <Textarea
            id={pasteId}
            rows={4}
            value={pasted}
            disabled={disabled || resolve.isPending}
            placeholder={t.pastePlaceholder}
            onChange={(event) => setPasted(event.target.value)}
          />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={disabled || resolve.isPending || parseEmails(pasted).length === 0}
              onClick={() =>
                resolve.mutate(parseEmails(pasted), {
                  onSuccess: (result) => {
                    add(result.found)
                    setNotFound(result.notFound)
                    setPasted(result.notFound.join('\n'))
                  }
                })
              }
            >
              {resolve.isPending ? t.resolving : t.resolve}
            </Button>
            <button
              type="button"
              onClick={() => {
                setMode('search')
                setNotFound([])
              }}
              className="text-[12.5px] text-fg-muted hover:text-fg"
            >
              {t.searchToggle}
            </button>
          </div>
          <div aria-live="polite" className="mt-2 space-y-1 text-[12.5px] leading-[1.5]">
            {resolve.isSuccess && <p className="text-fg-muted">{t.found(fmt, resolve.data.found.length)}</p>}
            {notFound.length > 0 && <p className="text-alert">{t.notFound(notFound)}</p>}
            {resolve.isError && <p className="text-alert">{t.failed}</p>}
          </div>
        </div>
      )}
    </div>
  )
}

/** A person's initials disc, in the same register as the shell's own avatar. */
export const Initials = ({ person, size }: { person: Pick<PersonRef, 'avatarInitials'>; size: 20 | 28 | 36 }) => (
  <span
    aria-hidden
    className="flex shrink-0 items-center justify-center rounded-full border border-rule bg-card font-semibold tracking-[0.02em] text-fg-muted"
    style={{ width: size, height: size, fontSize: size === 20 ? 8.5 : size === 28 ? 10 : 12 }}
  >
    {person.avatarInitials}
  </span>
)
